/**
 * API Service Layer - Abstracts all Supabase database operations.
 * Provides a clean, typed interface for data access with error handling.
 */

import { supabase } from "@/integrations/supabase/client";
import { safeSubscribe } from "@/services/ios-safari-fallback";
import { armadillo } from "@/services/security";
import { getServerNow } from "@/lib/serverTime";
import type { BountyRow, ProfileRow, ApiResult } from "@/types";
import { UI_CONFIG, STORAGE_CONFIG } from "@/config/constants";
import { formatStorageFileName, extractStoragePathFromUrl } from "@/lib/utils";
import { resolveProofFileMime, isAllowedProofMime } from "@/lib/proofUpload";

/**
 * Wallet & Deposit API Operations
 */
export const walletApi = {
  /**
   * Get user's wallet balance
   */
  async getBalance(userId: string): Promise<ApiResult<{
    balance_cents: number;
    total_deposited_cents: number;
    total_spent_cents: number;
    total_withdrawn_cents: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('get_wallet_balance', {
        p_user_id: userId,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Calculate task posting fee
   */
  async calculateTaskFee(userId: string, bountyAmountCents: number): Promise<ApiResult<{
    base_amount_cents: number;
    fee_cents: number;
    total_cost_cents: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('calculate_task_fee', {
        p_user_id: userId,
        p_bounty_amount_cents: bountyAmountCents,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Deduct from wallet for task posting
   */
  async deductForTask(userId: string, bountyId: string, bountyAmountCents: number): Promise<ApiResult<{
    success: boolean;
    message: string;
    fee_cents: number;
    total_deducted: number;
    remaining_balance: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('deduct_wallet_for_task', {
        p_user_id: userId,
        p_bounty_id: bountyId,
        p_bounty_amount_cents: bountyAmountCents,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Request withdrawal
   */
  async requestWithdrawal(userId: string, amountCents: number): Promise<ApiResult<{
    success: boolean;
    message: string;
    fee_cents: number;
    net_payout: number;
    remaining_balance: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('request_withdrawal', {
        p_user_id: userId,
        p_amount_cents: amountCents,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get deposit history
   */
  async getDepositHistory(userId: string): Promise<ApiResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('deposits')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get withdrawal history
   */
  async getWithdrawalHistory(userId: string): Promise<ApiResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('user_id', userId)
        .order('requested_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Bounty API Operations
 */
export const bountyApi = {
  /**
   * Fetch all bounties with optional filter
   */
  async getBounties(filter?: "open" | "all"): Promise<ApiResult<BountyRow[]>> {
    try {
      // PHOENIX ARCHITECTURE: For "open" filter, derive state from claim_requests data
      // Don't rely solely on bounty status column - check actual workflow state
      if (filter === "open") {
        // Direct query with PHOENIX logic: show bounties that are truly active
        const { data, error } = await supabase
          .from("bounties")
          .select("*")
          .or('status.eq.open,status.eq.claimed,status.eq.submitted')
          .order("created_at", { ascending: false })
          .limit(UI_CONFIG.BOUNTY_FEED_LIMIT);
        
        if (error) throw error;
        
        // PHOENIX: Filter out bounties that are EXPIRED (regardless of status)
        // Use SERVER time for consistent filtering across all devices
        const nowMs = getServerNow();
        
        const activeBounties = (data ?? []).filter(b => {
          const expiresAtMs = new Date(b.expires_at).getTime();
          return expiresAtMs > nowMs;
        });
        
        return { data: activeBounties as BountyRow[], error: null };
      }
      
      // For "all" filter, fetch everything
      let query = supabase
        .from("bounties")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(UI_CONFIG.BOUNTY_FEED_LIMIT);

      const { data, error } = await query;

      if (error) throw error;
      return { data: data as BountyRow[] ?? [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch a single bounty by ID
   */
  async getBountyById(id: string): Promise<ApiResult<BountyRow>> {
    try {
      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      return { data: data as BountyRow ?? null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Check if bounty can be extended
   */
  async canExtendBounty(bountyId: string, userId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('can_extend_bounty', {
        p_bounty_id: bountyId,
        p_user_id: userId
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Calculate extension cost
   */
  async calculateExtensionCost(userId: string, hours: number): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('calculate_extension_cost', {
        p_user_id: userId,
        p_hours: hours
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Extend bounty with wallet payment
   */
  async extendBounty(bountyId: string, userId: string, hours: number): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('extend_bounty', {
        p_bounty_id: bountyId,
        p_user_id: userId,
        p_hours: hours
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Atomically charge the wallet and create a bounty in a single RPC.
   *
   * Replaces the old "deduct_wallet_balance then insert" pair, which could
   * leave a poster charged for a bounty that never got created. The server
   * computes the charge (reward pool + service fee + featured) from the
   * caller's own is_pro flag, so this is wallet-only; the card path still
   * pays Stripe up front and creates the bounty afterwards.
   */
  async createBountyPaid(params: {
    requesterId: string;
    title: string;
    description: string;
    location: string | null;
    proofType: "photo" | "screenshot" | "link" | "text" | "video" | "document" | "audio";
    rewardCents: number;
    country: string;
    maxClaimers?: number;
    autoAccept?: boolean;
    instructions?: string | null;
    durationHours?: number;
    proofRequirements?: string[];
    isFeatured?: boolean;
    featuredDurationHours?: number;
    useFreeCredit?: boolean;
  }): Promise<ApiResult<{ id: string; amount_charged_cents: number; new_balance_cents: number; used_free_credit: boolean }>> {
    try {
      const { requesterId, title, description } = params;

      // RATE LIMITING: Max 10 bounties per hour
      const rateLimitOk = await armadillo.checkRateLimit(requesterId, 'create_bounty', 10);
      if (!rateLimitOk) {
        return { data: null, error: new Error('Rate limit exceeded. Please wait before creating more bounties.') };
      }

      // INPUT VALIDATION: Check title and description for threats
      const titleValidation = await armadillo.validateInput(title, requesterId, 'bounty.title');
      if (!titleValidation.allowed) {
        return { data: null, error: new Error(titleValidation.reason ? `${titleValidation.reason} in title` : 'Invalid title') };
      }

      const descValidation = await armadillo.validateInput(description, requesterId, 'bounty.description');
      if (!descValidation.allowed) {
        return { data: null, error: new Error(descValidation.reason ? `${descValidation.reason} in description` : 'Invalid description') };
      }

      // BEHAVIORAL TRACKING: Track bounty creation
      await armadillo.trackBehavior(requesterId, 'bounty_created', {
        title_length: title.length,
        reward_cents: params.rewardCents,
        country: params.country
      });

      const { data, error } = await supabase.rpc('create_bounty_paid', {
        p_title: titleValidation.sanitizedInput || title,
        p_description: descValidation.sanitizedInput || description,
        p_proof_type: params.proofType,
        p_reward_cents: params.rewardCents,
        p_max_claimers: params.maxClaimers ?? 1,
        p_duration_hours: params.durationHours ?? 24,
        p_location: params.location ?? null,
        p_country: params.country || null,
        p_auto_accept: params.autoAccept ?? false,
        p_instructions: params.instructions ?? null,
        p_proof_requirements: (params.proofRequirements && params.proofRequirements.length > 0) ? params.proofRequirements : [],
        p_is_featured: params.isFeatured ?? false,
        p_featured_duration_hours: params.featuredDurationHours ?? 24,
        p_use_free_credit: params.useFreeCredit ?? false,
      } as any);

      if (error) throw error;

      const result = data as any;
      if (!result || result.success === false) {
        throw new Error(result?.error || 'Failed to create bounty');
      }

      // TRUST UPDATE: Reward successful bounty creation
      await armadillo.updateTrustForAction(requesterId, 'bounty_posted', true);

      return {
        data: {
          id: result.bounty_id as string,
          amount_charged_cents: result.amount_charged_cents as number,
          new_balance_cents: result.new_balance_cents as number,
          used_free_credit: Boolean(result.used_free_credit),
        },
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * @deprecated Use requestClaim instead. Delegates to request_bounty_claim RPC.
   */
  async claimBounty(bountyId: string): Promise<ApiResult<{ success: boolean; auto_accepted?: boolean }>> {
    return this.requestClaim(bountyId);
  },

  /**
   * Submit proof for a bounty
   */
  async submitProof(
    bountyId: string,
    proofUrl: string | null,
    proofText: string | null
  ): Promise<ApiResult<{
    success: boolean;
    message?: string;
    error?: string;
    auto_approved?: boolean;
    // Set when the poster has auto-approve on but the payout could not be
    // completed. The proof is still saved and falls back to manual review.
    auto_approve_error?: string;
  }>> {
    try {
      // Get current user for security checks
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // RATE LIMITING: Max 15 proofs per hour
        const rateLimitOk = await armadillo.checkRateLimit(user.id, 'submit_proof', 15);
        if (!rateLimitOk) {
          return { data: null, error: new Error('Rate limit exceeded. Please slow down.') };
        }

        // INPUT VALIDATION: Check proof text for threats
        if (proofText) {
          const proofValidation = await armadillo.validateInput(proofText, user.id, 'proof.text');
          if (!proofValidation.allowed) {
            return { data: null, error: new Error(proofValidation.reason ? `${proofValidation.reason} in proof text` : 'Invalid proof text') };
          }
          proofText = proofValidation.sanitizedInput || proofText;
        }

        // BEHAVIORAL TRACKING
        await armadillo.trackBehavior(user.id, 'proof_submitted', { 
          bounty_id: bountyId,
          has_url: !!proofUrl,
          text_length: proofText?.length || 0
        });
      }

      const { data, error } = await (supabase.rpc as any)("submit_proof", {
        p_bounty_id: bountyId,
        p_proof_url: proofUrl,
        p_proof_text: proofText,
      });

      if (error) throw error;
      
      // TRUST UPDATE
      if (user) {
        await armadillo.updateTrustForAction(user.id, 'proof_submitted', true);
      }
      
      return { data: data as { success: boolean; message?: string; error?: string; auto_approved?: boolean }, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Owner approves or rejects submitted proof for a claim
   */
  async reviewProof(
    claimId: string,
    action: 'approve' | 'reject',
    reason?: string
  ): Promise<ApiResult<{
    success: boolean;
    action?: string;
    error?: string;
    reward_cents?: number;
    resubmission_count?: number;
    max_resubmissions?: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('review_proof', {
        p_claim_id: claimId,
        p_action: action,
        p_reason: reason || null,
      });

      if (error) throw error;
      return { data: data as any, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Approve a bounty and process payout
   */
  async approveBounty(bountyId: string): Promise<ApiResult<{ success: boolean; message?: string; error?: string }>> {
    try {
      const { data, error } = await (supabase.rpc as any)("approve_bounty", {
        _bounty_id: bountyId,
      });

      if (error) throw error;
      return { data: data as { success: boolean; message?: string; error?: string }, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Cancel a bounty and return the unspent reward escrow to the poster.
   *
   * Replaces deleting the row directly: posting escrows reward x max_claimers,
   * so a raw delete discarded the poster's money along with the record.
   */
  async cancelBounty(
    bountyId: string,
    reason?: string
  ): Promise<ApiResult<{ success: boolean; refund_cents?: number; error?: string; pending_proofs?: number }>> {
    try {
      const { data, error } = await (supabase.rpc as any)("cancel_bounty", {
        p_bounty_id: bountyId,
        p_reason: reason ?? null,
      });

      if (error) throw error;
      return {
        data: data as { success: boolean; refund_cents?: number; error?: string; pending_proofs?: number },
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Subscribe to bounty changes (real-time)
   * iOS Safari: Uses safe subscription with fallback
   */
  subscribeToBounties(callback: () => void) {
    // iOS Safari safe subscription with fallback
    return safeSubscribe(
      supabase,
      "bounties-feed",
      "bounties",
      callback,
      {
        event: '*',
        schema: 'public'
      }
    );
  },

  /**
   * Request to claim a bounty (new workflow)
   */
  async requestClaim(bountyId: string, message?: string): Promise<ApiResult<{
    success: boolean;
    auto_accepted: boolean;
    error?: string;
  }>> {
    try {
      const { data, error } = await supabase.rpc('request_bounty_claim', {
        p_bounty_id: bountyId,
        p_message: message || null
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Accept or reject a claim request
   */
  async respondToClaim(claimId: string, action: 'accept' | 'reject'): Promise<ApiResult<{
    success: boolean;
    action: string;
    error?: string;
  }>> {
    try {
      const { data, error } = await supabase.rpc('respond_to_claim', {
        p_claim_id: claimId,
        p_action: action
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get all claim requests for a bounty
   */
  async getBountyClaims(bountyId: string): Promise<ApiResult<any[]>> {
    try {
      // Try RPC first (Phoenix Architecture)
      const { data, error } = await supabase.rpc('get_bounty_claims', {
        p_bounty_id: bountyId
      });

      if (error) {
        console.warn('[API] get_bounty_claims RPC failed, using direct query fallback:', error.message);
        // Fallback: Direct query without foreign key join (hunter_id references auth.users, not profiles)
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('claim_requests')
          .select(`
            id,
            bounty_id,
            hunter_id,
            status,
            message,
            proof_url,
            proof_text,
            proof_status,
            created_at,
            updated_at
          `)
          .eq('bounty_id', bountyId)
          .order('created_at', { ascending: false });

        if (fallbackError) throw fallbackError;
        
        // Fetch hunter profiles separately (hunter_id -> auth.users -> profiles)
        if (fallbackData && fallbackData.length > 0) {
          const hunterIds = fallbackData.map(row => row.hunter_id);
          const { data: hunters } = await supabase
            .from('profiles')
            .select('id, display_name, total_completed, total_earned_cents')
            .in('id', hunterIds);
          
          const hunterMap = new Map();
          hunters?.forEach(h => hunterMap.set(h.id, h));
          
          // Normalize the response to match RPC format
          const normalizedData = fallbackData.map(row => {
            const hunter = hunterMap.get(row.hunter_id);
            return {
              id: row.id,
              bounty_id: row.bounty_id,
              hunter_id: row.hunter_id,
              status: row.status,
              message: row.message,
              proof_url: row.proof_url,
              proof_text: row.proof_text,
              proof_status: row.proof_status || 'pending',
              created_at: row.created_at,
              updated_at: row.updated_at,
              hunter_name: hunter?.display_name || 'Unknown',
              hunter_total_completed: hunter?.total_completed || 0,
              hunter_total_earned_cents: hunter?.total_earned_cents || 0
            };
          });
          
          return { data: normalizedData, error: null };
        }
        
        return { data: [], error: null };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('[API] getBountyClaims error:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Check if bounty owner has sufficient balance for all potential hunter payouts
   */
  async checkOwnerBalance(bountyId: string): Promise<ApiResult<{
    sufficient: boolean;
    reason?: string;
    current_balance?: number;
    hunters_can_pay?: number;
    remaining_slots?: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('check_bounty_owner_balance', {
        p_bounty_id: bountyId
      });

      if (error) throw error;
      return { data: data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Send a bounty message (chat)
   */
  async sendBountyMessage(
    bountyId: string,
    receiverId: string,
    content: string
  ): Promise<ApiResult<any>> {
    try {
      console.log('📤 Sending message:', { bountyId, receiverId, content });
      const { data, error } = await supabase.rpc('send_bounty_message', {
        p_bounty_id: bountyId,
        p_receiver_id: receiverId,
        p_content: content
      });

      if (error) {
        console.error('❌ Send message error:', error);
        throw error;
      }
      
      // Check if RPC returned error in response body
      if (data && data.success === false) {
        console.error('❌ RPC authorization failed:', data.error);
        return { data: null, error: new Error(data.error || 'Authorization failed') };
      }
      
      console.log('✅ Message sent successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('❌ Send message failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update a message
   */
  async updateMessage(messageId: string, content: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', messageId);
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update message failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Delete a message
   */
  async deleteMessage(messageId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Delete message failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get bounty messages
   */
  async getBountyMessages(bountyId: string, userId: string): Promise<ApiResult<any[]>> {
    try {
      // Use the enriched view to get sender/receiver names
      const { data, error } = await supabase
        .from('bounty_messages_enriched')
        .select('*')
        .eq('bounty_id', bountyId)
        .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Enrich with display_name (fallback to username)
      const enrichedData = (data || []).map(msg => ({
        ...msg,
        sender_name: msg.sender_display_name || msg.sender_username || 'Unknown',
        receiver_name: msg.receiver_display_name || msg.receiver_username || 'Unknown',
      }));
      
      return { data: enrichedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Mark message as read
   */
    /**
   * Delete a bounty message (soft delete)
   */
  async deleteBountyMessage(messageId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('delete_bounty_message', {
        p_message_id: messageId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Edit a bounty message
   */
  async editBountyMessage(messageId: string, newContent: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('edit_bounty_message', {
        p_message_id: messageId,
        p_new_content: newContent
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Report a bounty message (create dispute)
   */
  async reportBountyMessage(messageId: string, reason: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('report_bounty_message', {
        p_message_id: messageId,
        p_reason: reason
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get disputes for admin panel
   */
  async getDisputes(status?: string): Promise<ApiResult<any[]>> {
    try {
      let query = supabase
        .from('disputes')
        .select('*');
      if (status) {
        query = query.eq('status', status);
      }
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Resolve a dispute (admin action)
   */
  async resolveDispute(disputeId: string, notes: string, action: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('resolve_dispute', {
        p_dispute_id: disputeId,
        p_resolution_notes: notes,
        p_resolution_action: action
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Set message as disappearing
   */
  async setMessageDisappearing(messageId: string, minutes: number = 300): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('set_message_disappearing', {
        p_message_id: messageId,
        p_disappear_in_minutes: minutes
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase
        .from('bounty_messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Subscribe to bounty messages (real-time)
   */
  subscribeToBountyMessages(bountyId: string, userId: string, callback: () => void) {
    const channel = supabase
      .channel(`bounty-messages-${bountyId}`)
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'bounty_messages',
          filter: `bounty_id=eq.${bountyId}`
        },
        callback
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Check if user can claim a bounty (fairness system)
   */
  async canClaimBounty(userId: string, bountyId: string): Promise<ApiResult<{
    can_claim: boolean;
    reason?: string;
    active_claims?: number;
    claim_limit?: number;
    remaining_slots?: number;
    grace_period?: boolean;
    warning?: string;
    abuse_detected?: boolean;
    recent_rejections?: number;
    cooldown_hours?: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('can_claim_bounty', {
        p_user_id: userId,
        p_bounty_id: bountyId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get hunter reputation info
   */
  async getHunterReputation(hunterId: string): Promise<ApiResult<{
    hunter_id: string;
    display_name: string | null;
    total_completed: number;
    total_earned_cents: number;
    is_pro: boolean;
    active_claims: number;
    total_claims: number;
    completion_rate: number | null;
    recent_rejections: number;
    claim_limit: number;
    member_since: string;
  }>> {
    try {
      const { data, error } = await supabase.rpc('get_hunter_reputation', {
        p_hunter_id: hunterId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Calculate user's claim limit
   */
  async calculateClaimLimit(userId: string): Promise<ApiResult<number>> {
    try {
      const { data, error } = await supabase.rpc('calculate_claim_limit', {
        p_user_id: userId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Count user's active claims
   */
  async countActiveClaims(userId: string): Promise<ApiResult<number>> {
    try {
      const { data, error } = await supabase.rpc('count_active_claims', {
        p_user_id: userId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Upload reference photo for a bounty
   */
  async uploadReferencePhoto(userId: string, bountyId: string, file: File): Promise<ApiResult<string>> {
    try {
      const timestamp = Date.now();
      const fileName = `${userId}/${bountyId}-${timestamp}-${file.name}`;
      
      const { data, error } = await supabase.storage
        .from('bounty-reference-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('bounty-reference-photos')
        .getPublicUrl(fileName);

      return { data: publicUrl, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Add reference photo URL to bounty
   */
  async addReferencePhoto(bountyId: string, photoUrl: string): Promise<ApiResult<void>> {
    try {
      // First, get current reference_photos array
      const { data: currentBounty, error: fetchError } = await supabase
        .from('bounties')
        .select('reference_photos')
        .eq('id', bountyId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Append new photo to existing array (or create new array if null)
      const currentPhotos = currentBounty?.reference_photos || [];
      const updatedPhotos = [...currentPhotos, photoUrl];
      
      // Update with new array
      const { error } = await supabase
        .from('bounties')
        .update({ reference_photos: updatedPhotos })
        .eq('id', bountyId);

      if (error) throw error;
      return { data: undefined, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Remove reference photo from bounty
   */
  async removeReferencePhoto(bountyId: string, photoUrl: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase
        .from('bounties')
        .update({
          reference_photos: supabase.raw('array_remove(reference_photos, ?)', [photoUrl])
        } as any)
        .eq('id', bountyId);

      if (error) throw error;
      return { data: undefined, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Delete reference photo from storage
   */
  async deleteReferencePhoto(photoUrl: string): Promise<ApiResult<void>> {
    try {
      // Extract file path from URL
      const urlParts = photoUrl.split('/storage/v1/object/public/bounty-reference-photos/');
      if (urlParts.length < 2) throw new Error('Invalid photo URL');
      
      const filePath = urlParts[1];
      
      const { error } = await supabase.storage
        .from('bounty-reference-photos')
        .remove([filePath]);

      if (error) throw error;
      return { data: undefined, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Profile API Operations
 */
export const profileApi = {
  /**
   * Get user profile
   */
  async getProfile(userId: string): Promise<ApiResult<ProfileRow>> {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name, total_completed, total_earned_cents, id, created_at")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      return { data: data as ProfileRow ?? null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get bounties posted by user
   */
  async getPostedBounties(userId: string): Promise<ApiResult<BountyRow[]>> {
    try {
      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("requester_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return { data: data as BountyRow[] ?? [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get bounties claimed by user
   */
  async getHuntedBounties(userId: string): Promise<ApiResult<BountyRow[]>> {
    try {
      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("claimed_by", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return { data: data as BountyRow[] ?? [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Storage API Operations
 */
export const storageApi = {
  /**
   * Upload proof file with validation
   * Supports single file upload (images, PDFs, documents)
   */
  async uploadProof(userId: string, bountyId: string, file: File): Promise<ApiResult<string>> {
    try {
      const mime = resolveProofFileMime(file);

      if (!isAllowedProofMime(mime)) {
        return {
          data: null,
          error: new Error(
            `File type not allowed (${mime || "unknown"}). Use images, MP4/WebM video, audio, or PDF/documents.`
          ),
        };
      }

      const isVideo = mime.startsWith("video/");
      const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;

      if (file.size > maxSize) {
        return {
          data: null,
          error: new Error(
            `File size exceeds ${isVideo ? "100" : "50"}MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
          ),
        };
      }

      const blockedExtensions = ["exe", "bat", "sh", "cmd", "com", "scr", "js", "vbs"];
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (extension && blockedExtensions.includes(extension)) {
        return { data: null, error: new Error("Executable files are not allowed for security reasons") };
      }

      const path = formatStorageFileName(userId, bountyId, file.name);
      const { error } = await supabase.storage.from(STORAGE_CONFIG.PROOFS_BUCKET).upload(path, file, {
        contentType: mime,
        upsert: false,
      });

      if (error) throw error;
      return { data: path, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Generate signed URL for viewing proof files
   * Handles multiple storage buckets: proofs, bounty-reference-photos
   */
  async getSignedProofUrl(proofUrl: string): Promise<ApiResult<string>> {
    try {
      console.log('[Storage] getSignedProofUrl called with:', proofUrl);
      
      // If it's already a full URL, extract the bucket and path
      // Otherwise, treat it as a raw storage path
      let bucketName = STORAGE_CONFIG.PROOFS_BUCKET;
      let path: string | null = null;
      
      if (proofUrl.startsWith('http')) {
        // Check if it's from bounty-reference-photos bucket
        if (proofUrl.includes('/bounty-reference-photos/')) {
          // This is a public bucket - return URL as-is (no signing needed)
          console.log('[Storage] Public bucket URL detected, returning as-is');
          return { data: proofUrl, error: null };
        }
        
        // Extract path from proofs bucket URL
        path = extractStoragePathFromUrl(proofUrl, STORAGE_CONFIG.PROOFS_BUCKET);
        console.log('[Storage] Extracted path from URL:', path);
      } else {
        // It's already a storage path, use it directly
        path = proofUrl;
        console.log('[Storage] Using raw path:', path);
      }
      
      if (!path) {
        console.error('[Storage] Invalid proof URL format:', proofUrl);
        throw new Error("Invalid proof URL format");
      }

      console.log('[Storage] Creating signed URL for path:', path);
      const { data, error } = await supabase
        .storage
        .from(bucketName)
        .createSignedUrl(path, STORAGE_CONFIG.SIGNED_URL_EXPIRY_SECONDS);

      if (error) {
        console.error('[Storage] Failed to create signed URL:', error);
        throw error;
      }
      
      console.log('[Storage] Signed URL created successfully');
      return { data: data?.signedUrl ?? null, error: null };
    } catch (error) {
      console.error('[Storage] getSignedProofUrl error:', error);
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Auth API Operations
 */
export const authApi = {
  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Sign out
   */
  async signOut(): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Pro & Subscription API Operations
 */
export const proApi = {
  /**
   * Create Stripe Checkout Session for subscription
   */
  async createSubscriptionCheckout(userId: string, tier: 'monthly' | 'yearly', priceCents: number, email: string): Promise<ApiResult<{ sessionId: string; url: string }>> {
    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: {
          userId,
          tier,
          priceCents,
          email,
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // upgradeToPro REMOVED: Must only be called server-side by Stripe webhook
  // using service_role key. Client-side access was a security vulnerability.

  /**
   * Renew/extend existing Pro subscription
   */
  async renewSubscription(userId: string, tier: 'monthly' | 'yearly', amountCents: number): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase.rpc('renew_subscription', {
        user_id: userId,
        tier,
        amount_cents: amountCents,
      });

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get user's subscription status with expiration info
   */
  async getSubscriptionStatus(userId: string): Promise<ApiResult<{
    is_active: boolean;
    tier: string;
    expires_at: string | null;
    days_remaining: number | null;
  }>> {
    try {
      const { data, error } = await supabase.rpc('get_subscription_status', {
        user_id: userId,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get user's subscription status (legacy - for backward compatibility)
   */
  async getSubscription(userId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Cancel subscription - Immediate downgrade
   */
  async cancelSubscription(
    subscriptionId: string, 
    userId: string, 
    refundAmount: number = 0,
    refundReason: string | null = null
  ): Promise<ApiResult<{
    success: boolean;
    message: string;
    cooling_off_until: string | null;
    can_upgrade_at: string | null;
  }>> {
    try {
      // Use the new anti-abuse cancellation function
      const { data, error } = await supabase.rpc('cancel_subscription_with_protection', {
        p_subscription_id: subscriptionId,
        p_user_id: userId,
        p_refund_amount: refundAmount,
        p_refund_reason: refundReason
      });

      if (error) throw error;
      
      // Return the result from the function (success, message, cooling_off_until)
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Check if user can upgrade to Pro (cooling-off period check)
   */
  async canUserUpgrade(userId: string): Promise<ApiResult<{
    can_upgrade: boolean;
    reason: string;
    cooling_off_until: string | null;
    days_remaining: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('can_user_upgrade', {
        p_user_id: userId,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get subscription history
   */
  async getSubscriptionHistory(userId: string): Promise<ApiResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Analytics API Operations
 */
export const analyticsApi = {
  /**
   * Get bounty analytics for requester
   */
  async getBountyAnalytics(requesterId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('bounties')
        .select('id, status, views_count, created_at, completed_at')
        .eq('requester_id', requesterId);

      if (error) throw error;

      // Calculate analytics
      const bounties = (data as any[]) || [];
      const total = bounties.length;
      const completed = bounties.filter(b => b.status === 'completed').length;
      const totalViews = bounties.reduce((sum, b) => sum + (b.views_count || 0), 0);
      
      // Calculate average completion time
      let totalHours = 0;
      let completedCount = 0;
      bounties.forEach(b => {
        if (b.completed_at && b.created_at) {
          const hours = (new Date(b.completed_at).getTime() - new Date(b.created_at).getTime()) / (1000 * 60 * 60);
          totalHours += hours;
          completedCount++;
        }
      });

      // Count total claims across all user's bounties
      let totalClaims = 0;
      const bountyIds = bounties.map(b => b.id);
      if (bountyIds.length > 0) {
        const { count, error: claimsError } = await supabase
          .from('claim_requests')
          .select('id', { count: 'exact', head: true })
          .in('bounty_id', bountyIds);
        
        if (!claimsError) {
          totalClaims = count || 0;
        }
      }

      const analytics = {
        total_bounties: total,
        completed_bounties: completed,
        total_views: totalViews,
        total_claims: totalClaims,
        total_completions: completed,
        completion_rate: total > 0 ? (completed / total) * 100 : 0,
        avg_completion_time_hours: completedCount > 0 ? totalHours / completedCount : 0,
      };

      return { data: analytics, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Track bounty view
   */
  async trackBountyView(bountyId: string, viewerId?: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase
        .from('bounty_views')
        .insert({
          bounty_id: bountyId,
          viewer_id: viewerId || null,
        });

      if (error) throw error;

      // Increment views count
      await supabase
        .from('bounties')
        .update({ views_count: supabase.rpc('increment_views') })
        .eq('id', bountyId);

      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get user profile stats
   */
  async getUserStats(userId: string): Promise<ApiResult<any>> {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('total_completed, total_earned_cents, total_spent_cents')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;

      const { data: postedBounties, error: bountyError } = await supabase
        .from('bounties')
        .select('reward_cents')
        .eq('requester_id', userId);

      if (bountyError) throw bountyError;

      const totalSpent = (postedBounties || []).reduce((sum, b) => sum + (b.reward_cents || 0), 0);
      const totalPosted = postedBounties?.length || 0;

      return {
        data: {
          total_bounties_posted: totalPosted,
          total_bounties_completed: profile?.total_completed || 0,
          total_spent: totalSpent,
          total_earned: profile?.total_earned_cents || 0,
          avg_bounty_value: totalPosted > 0 ? totalSpent / totalPosted : 0,
          completion_rate: profile?.total_completed || 0,
        },
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Messaging API Operations (Pro Feature)
 */
export const messagingApi = {
  /**
   * Send a message
   */
  async sendMessage(
    bountyId: string,
    senderId: string,
    receiverId: string,
    content: string,
    attachmentUrl?: string | null
  ): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          bounty_id: bountyId,
          sender_id: senderId,
          receiver_id: receiverId,
          content,
          attachment_url: attachmentUrl || null,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Send email notification to recipient
      try {
        await (supabase.rpc as any)('send_new_message_notification', {
          p_message_id: data.id,
          p_recipient_id: receiverId,
          p_sender_id: senderId,
          p_message_text: content,
          p_conversation_id: bountyId
        });
      } catch (err) {
        console.warn('Failed to send new message email notification:', err);
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId: string): Promise<ApiResult<void>> {
    try {
      const { error } = await supabase
        .from('bounty_messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Subscribe to new messages (real-time)
   */
  subscribeToMessages(bountyId: string, userId: string, callback: () => void) {
    const channel = supabase
      .channel(`messages-${bountyId}`)
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'messages',
          filter: `bounty_id=eq.${bountyId}`
        },
        callback
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};

/**
 * Bounty Wall API Operations
 */
export const wallApi = {
  /**
   * Publish an accepted proof as a public wall entry (poster-only, RPC-validated)
   */
  async publishWallEntry(params: {
    claimId: string;
    title: string;
    description?: string | null;
    photos: { url: string; caption?: string | null }[];
    city?: string | null;
    country?: string | null;
  }): Promise<ApiResult<{ wall_id: string; slug: string; published_at: string }>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: new Error('Not authenticated') };
      }

      // RATE LIMITING: Max 20 wall publishes per hour
      const rateLimitOk = await armadillo.checkRateLimit(user.id, 'publish_wall_entry', 20);
      if (!rateLimitOk) {
        return { data: null, error: new Error('Rate limit exceeded. Please wait before publishing more entries.') };
      }

      // INPUT VALIDATION: Check title and description for threats
      const titleValidation = await armadillo.validateInput(params.title, user.id, 'wall.title');
      if (!titleValidation.allowed) {
        return { data: null, error: new Error(titleValidation.reason ? `${titleValidation.reason} in wall title` : 'Invalid title') };
      }
      let description = params.description || null;
      if (description) {
        const descValidation = await armadillo.validateInput(description, user.id, 'wall.description');
        if (!descValidation.allowed) {
          return { data: null, error: new Error(descValidation.reason ? `${descValidation.reason} in wall description` : 'Invalid description') };
        }
        description = descValidation.sanitizedInput || description;
      }

      const { data, error } = await supabase.rpc('publish_wall_entry', {
        p_claim_id: params.claimId,
        p_title: titleValidation.sanitizedInput || params.title,
        p_description: description,
        p_photos: params.photos,
        p_city: params.city || null,
        p_country: params.country || null,
      } as any);

      if (error) throw error;
      const result = data as any;
      if (!result || result.success === false) {
        throw new Error(result?.error || 'Failed to publish wall entry');
      }

      // TRUST UPDATE: Reward successful wall publication
      await armadillo.updateTrustForAction(user.id, 'wall_published', true);

      return {
        data: {
          wall_id: result.wall_id as string,
          slug: result.slug as string,
          published_at: result.published_at as string,
        },
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get published wall entries (public, no auth required)
   */
  async getWallEntries(limit: number = 60, country?: string | null): Promise<ApiResult<any[]>> {
    try {
      let query = supabase
        .from('wall_entries')
        .select(`*, hunter:profiles!wall_entries_hunter_id_fkey(display_name, avatar_url, total_completed)`)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (country) {
        query = query.eq('country', country);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data as any[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get a single wall entry by slug (public)
   */
  async getWallEntryBySlug(slug: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('wall_entries')
        .select(`*, hunter:profiles!wall_entries_hunter_id_fkey(display_name, avatar_url, total_completed), poster:profiles!wall_entries_poster_id_fkey(display_name, avatar_url)`)
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return { data: data as any ?? null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get wall entry by bounty id (popup prefill / "published" badge)
   */
  async getWallEntryByBountyId(bountyId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase
        .from('wall_entries')
        .select(`*, hunter:profiles!wall_entries_hunter_id_fkey(display_name, avatar_url)`)
        .eq('bounty_id', bountyId)
        .maybeSingle();

      if (error) throw error;
      return { data: data as any ?? null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Increment public view counter (no auth required)
   */
  async incrementWallView(wallId: string): Promise<ApiResult<any>> {
    try {
      const { data, error } = await supabase.rpc('increment_wall_view', {
        p_wall_id: wallId
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};

export { backerApi } from "./backerApi";
